import {
  type PrimaryObjectUploadContextType,
  defaultPrimaryObjectUploadContext,
  defaultSmartConnectorFlow,
} from '../context';
import { useAsync, useAsyncFn } from 'react-use';
import { useHistory, useParams } from 'react-router-dom';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  FlowLoadErrors,
  PrimaryObjectUploadStep,
  SmartConnector,
  SmartConnectorBase,
  SmartConnectorFlow,
  SmartConnectorFlowErrors,
  CSVFlowLoadDTO,
  SmartConnectorMetaData,
  SmartConnectorStatus,
  SmartConnectorVariable,
  SmartConnectorFlowLoad,
  CSVFlowLoad,
} from 'pages/SmartConnectors/types';
import {
  AUTOMATION_TRIGGER_CONFIGS,
  PRIMARY_OBJECT_UPLOAD_STEPS,
  PRIMARY_OBJECT_UPLOAD_STEP_DATA_BY_STEP,
  SMARTCONNECTORS_PATHNAME,
  SMART_CONNECTOR_STATUSES,
} from 'pages/SmartConnectors/constants';
import SmartConnectorService from 'services/SmartConnectorService';
import { AxiosError } from 'axios';
import { isEqual } from 'lodash';
import {
  getSmartConnectorFlowLoadsDTO,
  getStepData,
  getValidFlowLoads,
} from '../helpers';
import CustomObjectsService from 'services/CustomObjectsService';
import { defaultSmartConnector } from 'pages/SmartConnectors/SmartConnector/SmartConnectorWizard/context';
import { KizenError } from 'services/errors';
import {
  getSmartConnectorVariables,
  isCSVLoadStep,
} from 'pages/SmartConnectors/SmartConnector/helpers';
import { useSmartConnectorTitle } from 'pages/SmartConnectors/SmartConnector/hooks/useSmartConnectorTitle';
import { useDeactivationModal } from 'pages/SmartConnectors/SmartConnector/hooks/useDeactivationModal';
import { getIsSmartConnectorActive } from 'pages/SmartConnectors/helpers';
import { useToast, toastVariant } from '__components/ToastProvider';

export const usePrimaryObjectUploadContext =
  (): PrimaryObjectUploadContextType => {
    const [showToast] = useToast();
    const { id, customObjectId, loadId } = useParams<{
      id: string;
      customObjectId: string;
      loadId: string;
    }>();
    const history = useHistory<{
      index: number;
      errors?: FlowLoadErrors;
      scope?: string;
      step: PrimaryObjectUploadStep;
    }>();

    if (!loadId) {
      throw new KizenError(404);
    }

    // state //////////////////////////////////////////////////////////

    const [smartConnector, setSmartConnector] = useState<
      SmartConnectorBase | SmartConnector
    >(defaultSmartConnector);

    const [step, setStep] = useState<PrimaryObjectUploadStep>(
      history.location.state?.step || PRIMARY_OBJECT_UPLOAD_STEPS.matching
    );

    const [nextStep, setNextStep] = useState<PrimaryObjectUploadStep>(
      PRIMARY_OBJECT_UPLOAD_STEPS.matching
    );

    const [isDirty, setIsDirty] = useState<boolean>(false);

    const [isValid, setIsValid] = useState<boolean>(true);

    const [refreshing, setRefreshing] = useState<boolean>(false);

    const [stepData, setStepData] = useState<Partial<SmartConnectorFlow>>({});

    const [errors, setErrors] = useState<SmartConnectorFlowErrors>({
      loads: [],
    });

    const { deactivationModalProps, showDeactivationModalWithResult } =
      useDeactivationModal<boolean | null>({ initial: null });

    // refs //////////////////////////////////////////////////////////

    const isNew = useRef<boolean>(loadId === 'new');

    const loadIndexRef = useRef(history.location.state?.index || 0);
    const scopeRef = useRef(history.location.state?.scope || '');

    const smartConnectorRef = useRef(smartConnector);
    smartConnectorRef.current = smartConnector;

    const stepRef = useRef(step);
    stepRef.current = step;

    const stepDataRef = useRef(stepData);
    stepDataRef.current = stepData;

    // effects /////////////////////////////////////////////////////////

    useSmartConnectorTitle(smartConnector);

    const [{ loading }, fetchSmartConnector] = useAsyncFn(
      async () => {
        const response = await SmartConnectorService.getSmartConnector(id);
        const data: SmartConnector = response.data;

        // we can have loads with invalid scope that was removed (renamed),
        // also smartConnector can have execution varaibles with invalid scope
        // BE wont't remove instances with invalid scope, so we need to fix it here on the fly
        const loads = getValidFlowLoads(data, loadId, scopeRef.current);

        const smartConnector: SmartConnector = {
          ...data,
          flow: { ...(data.flow || defaultSmartConnectorFlow), loads },
        };

        setSmartConnector(smartConnector);

        if (loadId === 'new') {
          if (!loads.length) {
            loadIndexRef.current = 0;
          }

          loads.splice(loadIndexRef.current, 0, {
            custom_object: customObjectId,
            type: 'csv_load',
            order: 0,
            field_mapping_rules: [],
            matching_rules: [],
            scope: scopeRef.current,
            automation_trigger_config: AUTOMATION_TRIGGER_CONFIGS.fire_all,
          });
        }

        const errorsFromHistory = history.location.state?.errors || {};

        setErrors((prev) => ({
          ...prev,
          loads: loads.map(({ id }) => {
            return id === loadId
              ? errorsFromHistory
              : {
                  execution_variable: {},
                };
          }),
        }));

        history.replace(history.location.pathname);

        setStepData(
          getStepData(
            {
              ...smartConnector.flow,
              loads,
            },
            stepRef.current
          )
        );
        return data;
      },
      [id, customObjectId],
      { loading: true }
    );

    const {
      value: metaData = defaultPrimaryObjectUploadContext.metaData,
      loading: loadingMetaData,
    } = useAsync<() => Promise<SmartConnectorMetaData>>(async () => {
      const { data } = await SmartConnectorService.getSmartConnectorMetaData();
      return data;
    });

    const { value: customObject, loading: loadingObject } =
      useAsync(async () => {
        const [model, fields] = await Promise.all([
          CustomObjectsService.getCustomObjectDetails(customObjectId),
          CustomObjectsService.getObjectFieldsWithoutRestrictions(
            customObjectId
          ),
        ]);

        return { ...model, fields };
      }, [customObjectId]);

    useEffect(() => {
      fetchSmartConnector();
    }, [fetchSmartConnector]);

    useLayoutEffect(() => {
      setNextStep(step);
      //initiates stepData for each step
      setStepData(getStepData(smartConnectorRef.current.flow, step));
    }, [step]);

    useEffect(() => {
      const fields = PRIMARY_OBJECT_UPLOAD_STEP_DATA_BY_STEP[step];
      const isDirty = fields.some((field: keyof SmartConnectorFlow) => {
        return !isEqual(smartConnector.flow[field], stepData[field]);
      });
      setIsDirty(isDirty);
    }, [smartConnector, step, stepData]);

    // memoized ////////////////////////////////////////////////////////

    const [loadIndex, scope] = useMemo(() => {
      const loadStep = (stepData.loads || []).find(
        (load): load is CSVFlowLoad =>
          isCSVLoadStep(load) &&
          ((loadId === 'new' && !load.id) || load.id === loadId)
      );
      return [
        loadStep ? (stepData.loads || []).indexOf(loadStep) : -1,
        loadStep?.scope || '',
      ];
    }, [stepData.loads, loadId]);

    const [allVariables, variablesById] = useMemo(() => {
      const variables = getSmartConnectorVariables({
        execution_variables: (smartConnector.execution_variables || []).filter(
          (variable) => variable.scope === scope
        ),
        flow: {
          ...smartConnector.flow,
          loads: (smartConnector.flow.loads || []).reduce<
            SmartConnectorFlowLoad[]
          >((acc, load) => {
            if (isCSVLoadStep(load) && load.scope === scope) {
              acc.push(load);
            }
            if (!isCSVLoadStep(load) && load.return_scopes?.length) {
              const returnScopes = load.return_scopes.filter(
                (returnScope) => returnScope.name === scope
              );
              if (returnScopes.length) {
                acc.push({ ...load, return_scopes: returnScopes });
              }
            }
            return acc;
          }, []),
        },
      });

      return [
        variables,
        variables.reduce(
          (acc, variable) => {
            acc[variable.id] = variable;
            return acc;
          },
          {} as Record<string, SmartConnectorVariable>
        ),
      ];
    }, [smartConnector, scope]);

    // handlers //////////////////////////////////////////////////////////

    const handleGoToStep = useCallback(
      (step: PrimaryObjectUploadStep) => {
        if (isDirty) {
          setNextStep(step);
        } else {
          setStep(step);
        }
      },
      [isDirty]
    );

    const patchSmartConnector = useCallback(
      async (
        payload: {
          flow: { loads?: Partial<CSVFlowLoadDTO>[] };
          status?: SmartConnectorStatus;
        },
        withClose: boolean
      ) => {
        try {
          setRefreshing(true);
          setErrors({
            loads: payload.flow.loads?.map(() => ({})) || [],
          });

          const { data } = await SmartConnectorService.updateSmartConnector(
            id,
            payload
          );
          setSmartConnector(data);
          setStepData(getStepData(data.flow, step));
          setIsDirty(false);
          if (isNew.current && !withClose) {
            if (step === PRIMARY_OBJECT_UPLOAD_STEPS.matching) {
              setStep(PRIMARY_OBJECT_UPLOAD_STEPS.mapping);

              if (loadId === 'new') {
                setTimeout(() =>
                  history.replace(
                    `${SMARTCONNECTORS_PATHNAME}/${id}/builder/upload-object/${customObjectId}/${data.flow.loads[loadIndexRef.current].id}`
                  )
                );
              }
            }
            if (step === PRIMARY_OBJECT_UPLOAD_STEPS.mapping) {
              setStep(PRIMARY_OBJECT_UPLOAD_STEPS.preview);
            }
          }

          if (withClose) {
            setTimeout(() =>
              history.push(`${SMARTCONNECTORS_PATHNAME}/${id}/builder`)
            );
          }

          return true;
        } catch (error) {
          if (error instanceof AxiosError) {
            const axiosError = (
              error as AxiosError<{ flow: SmartConnectorFlowErrors }>
            ).response?.data;

            const errors: SmartConnectorFlowErrors = {};

            if (axiosError?.flow?.additional_variables) {
              errors.additional_variables =
                axiosError.flow.additional_variables;
            }

            if (axiosError?.flow?.loads) {
              errors.loads = axiosError.flow.loads;
              if (axiosError.flow.loads[loadIndex]?.no_rules) {
                showToast({
                  // @ts-expect-error - these types or usage should be fixed
                  message: axiosError.flow.loads[loadIndex].no_rules,
                  variant: toastVariant.FAILURE,
                });
                setErrors(errors);
                return null;
              }
            }

            setErrors(errors);
          }
          return false;
        } finally {
          setRefreshing(false);
        }
      },
      [id, step, history, customObjectId, loadId, loadIndex, showToast]
    );

    const handleSave = useCallback(
      (withClose: boolean): Promise<boolean | null> => {
        if (
          isDirty &&
          getIsSmartConnectorActive({
            status: smartConnector.status,
          })
        ) {
          return showDeactivationModalWithResult({
            handleSave: () =>
              patchSmartConnector(
                {
                  ...getSmartConnectorFlowLoadsDTO(
                    stepDataRef.current,
                    loadId,
                    step
                  ),
                  status: SMART_CONNECTOR_STATUSES.inactive,
                },
                withClose
              ),
            handleDiscard: () => {
              setSmartConnector(smartConnectorRef.current);
              setStepData(
                getStepData(smartConnectorRef.current.flow, stepRef.current)
              );
              setIsDirty(false);
              return null;
            },
          });
        }
        return patchSmartConnector(
          getSmartConnectorFlowLoadsDTO(stepDataRef.current, loadId, step),
          withClose
        );
      },
      [
        patchSmartConnector,
        loadId,
        step,
        smartConnector.status,
        showDeactivationModalWithResult,
        isDirty,
      ]
    );

    return {
      smartConnector,
      customObject,
      scope,
      loadId,
      loadIndex,
      metaData,
      loading: loading || loadingMetaData || loadingObject,
      refreshing,
      handleGoToStep,
      step,
      setStep,
      nextStep,
      setNextStep,
      isNew: isNew.current,
      isDirty,
      setIsDirty,
      handleSave,
      stepData,
      setStepData,
      errors,
      setErrors,
      isValid,
      setIsValid,
      allVariables,
      variablesById,
      deactivationModalProps,
    };
  };
